import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import surveyIllu from '@app/illu/Small/Survey.svg';
import calendarIllu from '@app/illu/Small/Calendar.svg';
import discussIllu from '@app/illu/Small/Discuss.svg';
import giftIllu from '@app/illu/Small/Gift.svg';
import emailIllu from '@app/illu/Small/SendEmail.svg';
import classnames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    borderRadius: theme.borderRadius.large,
    backgroundColor: theme.colors.white,
    width: '100%',
  },
  containerLarge: {
    padding: [theme.marginBase * 4, theme.marginBase],
    [theme.media.mobile]: {
      padding: 0,
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'relative',

    width: '100%',
    maxWidth: theme.marginBase * 90,
    margin: 'auto',

    [theme.media.mobile]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: theme.marginBase * 22,
    [theme.media.tablet]: {
      width: theme.marginBase * 16,
    },
    [theme.media.mobile]: {
      flexDirection: 'row',
      width: '100%',
    },
  },
  stepIllu: {
    width: theme.marginBase * 8,
    height: theme.marginBase * 8,
    borderRadius: theme.marginBase * 2,
    [theme.media.mobile]: {
      width: theme.marginBase * 6,
      height: theme.marginBase * 6,
      borderRadius: theme.marginBase + 2,
    },
  },
  smallIllu: {
    width: theme.marginBase * 6,
    height: theme.marginBase * 6,
  },
  stepText: {
    ...theme.fonts.caption,
    textAlign: 'center',
    marginTop: theme.marginBase * 2,
    [theme.media.mobile]: {
      textAlign: 'left',
      marginTop: 0,
      marginLeft: theme.marginBase,
    },
  },
  lineBetween: {
    flex: 1,
    height: 1,
    backgroundColor: theme.colors.grayBorder,
    margin: [0, -theme.marginBase * 5],
    marginTop: theme.marginBase * 4,
    [theme.media.tablet]: {
      margin: [0, -theme.marginBase * 3],
      marginTop: theme.marginBase * 4,
    },
  },
  lineBetweenSmall: {
    marginTop: theme.marginBase * 3,
  },
  lineBetweenMobile: {
    [theme.media.mobile]: {
      flex: 'none',
      width: 1,
      height: 16,
      backgroundColor: theme.colors.grayBorder,
      margin: 0,
      marginLeft: theme.marginBase * 3,
      marginTop: theme.marginBase / 2,
      marginBottom: theme.marginBase / 2,
    },
  },
}));

interface Props {
  noIncentive?: boolean;
  isUnmoderated?: boolean;
  className?: string;
  small?: boolean;
}

export const HowItWorksLine = ({
  noIncentive,
  isUnmoderated,
  className,
  small,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const steps = useMemo(() => {
    const AVAILABLE_STEP = {
      survey: {
        illustration: surveyIllu,
        illustrationAlt: 'Survey',
        label: t('application.howItWorks.questions'),
      },
      calendar: {
        illustration: calendarIllu,
        illustrationAlt: 'Calendar',
        label: t('application.howItWorks.appointment'),
      },
      freeTimeCalendar: {
        illustration: calendarIllu,
        illustrationAlt: 'Calendar',
        label: t('application.howItWorks.freeTime'),
      },
      discuss: {
        illustration: discussIllu,
        illustrationAlt: 'Discuss',
        label: t('application.howItWorks.test'),
      },
      gift: {
        illustration: giftIllu,
        illustrationAlt: 'Gift',
        label: t('application.howItWorks.counterpart'),
      },
      mail: {
        illustration: emailIllu,
        illustrationAlt: 'Email',
        label: t('application.howItWorks.emailLink'),
      },
    };

    if (isUnmoderated) {
      return [
        AVAILABLE_STEP.survey,
        AVAILABLE_STEP.mail,
        AVAILABLE_STEP.freeTimeCalendar,
        AVAILABLE_STEP.gift,
      ];
    }
    if (noIncentive) {
      return [
        AVAILABLE_STEP.survey,
        AVAILABLE_STEP.calendar,
        AVAILABLE_STEP.discuss,
      ];
    }
    return [
      AVAILABLE_STEP.survey,
      AVAILABLE_STEP.calendar,
      AVAILABLE_STEP.discuss,
      AVAILABLE_STEP.gift,
    ];
  }, [isUnmoderated, noIncentive, t]);

  return (
    <div
      className={classnames(
        classes.container,
        {
          [classes.containerLarge]: !small,
        },
        className,
      )}
    >
      <div className={classes.content}>
        {steps.map((step, index) => {
          const displayLine = index + 1 < steps.length;
          return [
            <div className={classes.step} key={step.illustrationAlt}>
              <img
                src={step.illustration}
                alt={step.illustrationAlt}
                className={classnames(classes.stepIllu, {
                  [classes.smallIllu]: !!small,
                })}
              />
              <div className={classes.stepText}>
                <strong>{`${index + 1}. `}</strong>
                {step.label}
              </div>
            </div>,
            displayLine && (
              <div
                className={classnames(
                  classes.lineBetween,
                  classes.lineBetweenMobile,
                  {
                    [classes.lineBetweenSmall]: !!small,
                  },
                )}
              />
            ),
          ];
        })}
      </div>
    </div>
  );
};
